import { useCallback, useEffect, useState } from 'react'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { StatusModal, useStatusModal } from '../../components/ui/StatusModal'
import * as discountCodesService from '../../services/discountCodesService'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'
import { buildStorefrontUrl } from '../../utils/buildStorefrontUrl'
import {
  buildLongCampaignMessage,
  buildShortCampaignMessage,
  copyTextToClipboard,
} from '../../utils/discountCampaignCopy'

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromDatetimeLocal(local: string): string | null {
  if (!local.trim()) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function statusBadgeClass(status: discountCodesService.DiscountCodeVisibilityStatus): string {
  switch (status) {
    case 'active_now':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    case 'scheduled':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    case 'expired':
      return 'bg-muted text-muted-foreground'
    case 'exhausted':
      return 'bg-orange-500/15 text-orange-800 dark:text-orange-300'
    case 'disabled':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function formatSchedule(row: discountCodesService.DiscountCodeRecord): string {
  if (!row.startsAt && !row.endsAt) return 'Always on'
  const start = row.startsAt ? new Date(row.startsAt).toLocaleString() : '—'
  const end = row.endsAt ? new Date(row.endsAt).toLocaleString() : '—'
  return `${start} → ${end}`
}

type FormState = {
  code: string
  discountType: 'percent' | 'fixed'
  discountValue: string
  minOrderSubtotal: string
  maxDiscountAmount: string
  startDate: string
  endDate: string
  usageLimit: string
  isActive: boolean
}

function emptyForm(): FormState {
  return {
    code: '',
    discountType: 'percent',
    discountValue: '10',
    minOrderSubtotal: '',
    maxDiscountAmount: '',
    startDate: '',
    endDate: '',
    usageLimit: '',
    isActive: true,
  }
}

export default function DiscountCodesPage() {
  const { openSuccess, openError, closeStatusModal, statusModalProps } = useStatusModal()
  const { currentStoreId: storeId, storeRecord } = useCurrentStore()
  const [items, setItems] = useState<discountCodesService.DiscountCodeRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [showCampaign, setShowCampaign] = useState(false)

  const selectedStore = storeRecord
  const currency = selectedStore?.currency ?? 'USD'
  const storefrontUrl = selectedStore ? buildStorefrontUrl(selectedStore.subdomain) : null

  const loadCodes = useCallback(async () => {
    if (storeId == null) return
    setLoading(true)
    setError(null)
    try {
      const res = await discountCodesService.listDiscountCodes(storeId)
      setItems(res.items)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load discount codes'))
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    setModalOpen(false)
    setEditingId(null)
    setShowCampaign(false)
  }, [storeId])

  useEffect(() => {
    void loadCodes()
  }, [loadCodes])

  const openCreate = () => {
    setForm(emptyForm())
    setEditingId(null)
    setShowCampaign(false)
    setModalOpen(true)
  }

  const openEdit = (row: discountCodesService.DiscountCodeRecord) => {
    setForm({
      code: row.code,
      discountType: row.discountType,
      discountValue: String(row.discountValue),
      minOrderSubtotal: row.minOrderSubtotal != null ? String(row.minOrderSubtotal) : '',
      maxDiscountAmount: row.maxDiscountAmount != null ? String(row.maxDiscountAmount) : '',
      startDate: toDatetimeLocal(row.startsAt),
      endDate: toDatetimeLocal(row.endsAt),
      usageLimit: row.usageLimit != null ? String(row.usageLimit) : '',
      isActive: row.isActive,
    })
    setEditingId(row.id)
    setShowCampaign(true)
    setModalOpen(true)
  }

  const buildPayload = () => {
    const startsAt = fromDatetimeLocal(form.startDate)
    const endsAt = fromDatetimeLocal(form.endDate)
    if (startsAt && endsAt && new Date(startsAt).getTime() >= new Date(endsAt).getTime()) {
      throw new Error('End date must be after start date')
    }
    const discountValue = Number(form.discountValue)
    if (!Number.isFinite(discountValue) || discountValue < 0) {
      throw new Error('Enter a valid discount value')
    }
    if (form.discountType === 'percent' && discountValue > 100) {
      throw new Error('Percent discount cannot exceed 100')
    }
    return {
      code: form.code.trim(),
      discountType: form.discountType,
      discountValue,
      minOrderSubtotal: form.minOrderSubtotal.trim() ? Number(form.minOrderSubtotal) : null,
      maxDiscountAmount:
        form.discountType === 'percent' && form.maxDiscountAmount.trim()
          ? Number(form.maxDiscountAmount)
          : null,
      startsAt,
      endsAt,
      usageLimit: form.usageLimit.trim() ? Number(form.usageLimit) : null,
      isActive: form.isActive,
    }
  }

  const handleSave = async () => {
    if (storeId == null) return
    setSaving(true)
    try {
      const payload = buildPayload()
      if (editingId == null) {
        await discountCodesService.createDiscountCode(storeId, payload)
        openSuccess({ title: 'Discount code created', description: 'Customers can use it at checkout.' })
      } else {
        await discountCodesService.updateDiscountCode(storeId, editingId, payload)
        openSuccess({ title: 'Discount code updated', description: 'Changes are saved.' })
      }
      setModalOpen(false)
      await loadCodes()
    } catch (e) {
      openError({
        title: 'Could not save',
        description: getUserFacingErrorMessage(e, 'Failed to save discount code'),
      })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (row: discountCodesService.DiscountCodeRecord) => {
    if (storeId == null) return
    try {
      await discountCodesService.updateDiscountCode(storeId, row.id, { isActive: !row.isActive })
      await loadCodes()
    } catch (e) {
      openError({
        title: 'Could not update',
        description: getUserFacingErrorMessage(e, 'Failed to update status'),
      })
    }
  }

  const handleDelete = async (row: discountCodesService.DiscountCodeRecord) => {
    if (storeId == null) return
    if (!window.confirm(`Delete code ${row.code}?`)) return
    try {
      await discountCodesService.deleteDiscountCode(storeId, row.id)
      openSuccess({ title: 'Discount code deleted', description: 'The code is no longer available.' })
      await loadCodes()
    } catch (e) {
      openError({
        title: 'Could not delete',
        description: getUserFacingErrorMessage(e, 'Failed to delete'),
      })
    }
  }

  const handleCopyCampaign = async (row: discountCodesService.DiscountCodeRecord, variant: 'short' | 'long') => {
    if (!selectedStore) return
    const record = row
    const msg =
      variant === 'short'
        ? buildShortCampaignMessage({
            storeName: selectedStore.name,
            storefrontUrl,
            record,
            currency,
          })
        : buildLongCampaignMessage({
            storeName: selectedStore.name,
            storefrontUrl,
            record,
            currency,
          })
    const ok = await copyTextToClipboard(msg)
    if (ok) openSuccess({ title: 'Copied', description: 'Campaign text is on your clipboard.' })
    else openError({ title: 'Copy failed', description: 'Could not copy to clipboard.' })
  }

  const previewRecord: discountCodesService.DiscountCodeRecord | null =
    editingId != null
      ? items.find((i) => i.id === editingId) ?? null
      : form.code.trim()
        ? {
            id: 0,
            storeId: storeId ?? 0,
            code: form.code.trim().toUpperCase(),
            discountType: form.discountType,
            discountValue: Number(form.discountValue) || 0,
            minOrderSubtotal: form.minOrderSubtotal.trim() ? Number(form.minOrderSubtotal) : null,
            maxDiscountAmount:
              form.discountType === 'percent' && form.maxDiscountAmount.trim()
                ? Number(form.maxDiscountAmount)
                : null,
            startsAt: fromDatetimeLocal(form.startDate),
            endsAt: fromDatetimeLocal(form.endDate),
            usageLimit: form.usageLimit.trim() ? Number(form.usageLimit) : null,
            timesUsed: 0,
            isActive: form.isActive,
            visibilityStatus: 'active_now',
            createdAt: '',
            updatedAt: '',
          }
        : null

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Discount codes"
          description="Checkout promo codes customers enter at payment. Separate from product special offers."
          action={
            <Button type="button" onClick={openCreate} disabled={storeId == null}>
              New code
            </Button>
          }
        />

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        {loading ? (
          <SkeletonListPage toolbarFields={0} columns={8} rows={4} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No discount codes yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="w-14 px-3 py-2">S/N</th>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Discount</th>
                  <th className="px-3 py-2">Min order</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Schedule</th>
                  <th className="px-3 py-2">Used</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, rowIndex) => (
                  <tr key={row.id} className="border-b border-border/60">
                    <td className="px-3 py-2 font-mono text-xs tabular-nums text-foreground">
                      {rowIndex + 1}
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold">{row.code}</td>
                    <td className="px-3 py-2">
                      {row.discountType === 'percent'
                        ? `${row.discountValue}%`
                        : `${row.discountValue}`}
                      {row.maxDiscountAmount != null ? (
                        <span className="text-muted-foreground"> (max {row.maxDiscountAmount})</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{row.minOrderSubtotal ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.visibilityStatus)}`}
                      >
                        {row.visibilityStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{formatSchedule(row)}</td>
                    <td className="px-3 py-2 text-xs">
                      {row.timesUsed}
                      {row.usageLimit != null ? ` / ${row.usageLimit}` : ''}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleCopyCampaign(row, 'short')}
                        >
                          Copy text
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleToggleActive(row)}
                        >
                          {row.isActive ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => void handleDelete(row)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId == null ? 'New discount code' : 'Edit discount code'}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="SUMMER20"
          />
          <Select
            label="Discount type"
            value={form.discountType}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                discountType: e.target.value as 'percent' | 'fixed',
              }))
            }
          >
            <option value="percent">Percent off order</option>
            <option value="fixed">Fixed amount off order</option>
          </Select>
          <Input
            label={form.discountType === 'percent' ? 'Percent (0–100)' : 'Amount off'}
            type="number"
            min={0}
            value={form.discountValue}
            onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
          />
          {form.discountType === 'percent' ? (
            <Input
              label="Maximum discount (optional)"
              type="number"
              min={0}
              value={form.maxDiscountAmount}
              onChange={(e) => setForm((f) => ({ ...f, maxDiscountAmount: e.target.value }))}
            />
          ) : null}
          <Input
            label="Minimum order amount (optional)"
            type="number"
            min={0}
            value={form.minOrderSubtotal}
            onChange={(e) => setForm((f) => ({ ...f, minOrderSubtotal: e.target.value }))}
          />
          <Input
            label="Usage limit (optional)"
            type="number"
            min={1}
            value={form.usageLimit}
            onChange={(e) => setForm((f) => ({ ...f, usageLimit: e.target.value }))}
            placeholder="Leave blank for unlimited"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Valid from (optional)"
              type="datetime-local"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
            />
            <Input
              label="Valid until (optional)"
              type="datetime-local"
              value={form.endDate}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
            />
          </div>
          <p className="text-xs text-muted-foreground">Leave dates blank to show immediately.</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            Active
          </label>

          {previewRecord && selectedStore ? (
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
              <button
                type="button"
                className="text-sm font-semibold text-foreground"
                onClick={() => setShowCampaign((v) => !v)}
              >
                Campaign message {showCampaign ? '▾' : '▸'}
              </button>
              {showCampaign ? (
                <>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Short</p>
                    <textarea
                      readOnly
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs min-h-[3rem]"
                      value={buildShortCampaignMessage({
                        storeName: selectedStore.name,
                        storefrontUrl,
                        record: previewRecord,
                        currency,
                      })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-1"
                      onClick={() =>
                        void handleCopyCampaign(previewRecord, 'short')
                      }
                    >
                      Copy short
                    </Button>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Long</p>
                    <textarea
                      readOnly
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs min-h-[5rem]"
                      value={buildLongCampaignMessage({
                        storeName: selectedStore.name,
                        storefrontUrl,
                        record: previewRecord,
                        currency,
                      })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-1"
                      onClick={() =>
                        void handleCopyCampaign(previewRecord, 'long')
                      }
                    >
                      Copy long
                    </Button>
                  </div>
                  {storefrontUrl ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        const ok = await copyTextToClipboard(storefrontUrl)
                        if (ok) openSuccess({ title: 'Copied', description: 'Store link is on your clipboard.' })
                        else openError({ title: 'Copy failed', description: 'Could not copy.' })
                      }}
                    >
                      Copy store link only
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </Modal>

      <StatusModal {...statusModalProps} onClose={closeStatusModal} />
    </div>
  )
}
